import { z } from 'zod';
import {
  enrollmentTokenViewSchema,
  errorEnvelopeSchema,
  gatewaySyncSchema,
  hostAuditPageSchema,
  issuedEnrollmentTokenSchema,
  opsResultSchema,
  pushStatsSchema,
  tenantHealthReportSchema,
  tenantListSchema,
  tenantMiddlewaresViewSchema,
  tenantMutationSchema,
  tenantViewSchema,
  updaterAcceptedSchema,
  updaterRunSchema,
  updatesViewSchema,
  welcomeEmailResultSchema,
  type AdminModules,
  type Branding,
  type CreateTenantInput,
  type EnrollmentTokenView,
  type GatewaySync,
  type HostAuditPage,
  type IssuedEnrollmentToken,
  type OpsResult,
  type PushStats,
  type TenantHealthReport,
  type TenantList,
  type TenantMiddlewaresView,
  type TenantMutation,
  type TenantView,
  type UpdateTenant,
  type UpdaterRun,
  type UpdatesView,
  type WelcomeEmailResult,
} from '@teachpartner/contracts';

/** Error envelope from central-api, with HTTP status. */
export class ApiError extends Error {
  constructor(
    public readonly status: number,
    public readonly code: string,
    message: string,
    public readonly details?: unknown,
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

export interface AuditFilter {
  readonly tenantCode?: string;
  readonly before?: number;
  readonly limit?: number;
}

/** Everything the console needs from `/v1/admin/*` (routes per CENTRAL_ROUTES). */
export interface HostAdminApi {
  listTenants(): Promise<TenantList>;
  getTenant(code: string): Promise<TenantView>;
  createTenant(input: CreateTenantInput): Promise<TenantMutation>;
  updateTenant(code: string, patch: UpdateTenant): Promise<TenantMutation>;
  deleteTenant(code: string): Promise<void>;
  issueEnrollmentToken(code: string): Promise<IssuedEnrollmentToken>;
  revokeEnrollmentToken(code: string): Promise<EnrollmentTokenView>;
  /** Resend the first Tenant Admin's welcome email (only before the school enrolls). */
  resendWelcomeEmail(code: string): Promise<WelcomeEmailResult>;
  health(code: string, refresh: boolean): Promise<TenantHealthReport>;
  opsBranding(code: string, body: Branding): Promise<OpsResult>;
  opsModules(code: string, body: AdminModules): Promise<OpsResult>;
  /** Plug-in middlewares the school may use, and what the school last reported. */
  middlewares(code: string): Promise<TenantMiddlewaresView>;
  /** Replaces the allow-list and pushes it to the school (a failed push is retried by central). */
  setMiddlewares(code: string, allowed: readonly string[]): Promise<TenantMiddlewaresView>;
  reloadGateway(): Promise<GatewaySync>;
  pushStats(days: number): Promise<PushStats>;
  audit(filter: AuditFilter): Promise<HostAuditPage>;
  /** Central updater state (or why it can't be reached). */
  getUpdates(): Promise<UpdatesView>;
  getUpdateRun(id: string): Promise<UpdaterRun>;
  /** The three actions answer 202 with the new run; 409 while a run is in progress. */
  checkUpdates(): Promise<UpdateAccepted>;
  deployRelease(version: string): Promise<UpdateAccepted>;
  rollbackRelease(): Promise<UpdateAccepted>;
}

export type UpdateAccepted = z.infer<typeof updaterAcceptedSchema>;

export interface HttpApiOptions {
  readonly baseUrl: string;
  readonly getToken: () => Promise<string | null>;
  readonly fetch?: typeof fetch;
}

export function httpApi(options: HttpApiOptions): HostAdminApi {
  const doFetch = options.fetch ?? fetch;

  async function call<T>(method: string, path: string, schema: z.ZodType<T>, body?: unknown) {
    const token = await options.getToken();
    const response = await doFetch(`${options.baseUrl}/v1/admin${path}`, {
      method,
      headers: {
        accept: 'application/json',
        ...(token ? { authorization: `Bearer ${token}` } : {}),
        ...(body === undefined ? {} : { 'content-type': 'application/json' }),
      },
      body: body === undefined ? undefined : JSON.stringify(body),
    });
    const payload: unknown = await response.json().catch(() => null);
    if (!response.ok) throw toApiError(response.status, payload);
    return schema.parse(payload);
  }

  const t = (code: string) => `/tenants/${encodeURIComponent(code)}`;
  return {
    listTenants: () => call('GET', '/tenants', tenantListSchema),
    getTenant: (code) => call('GET', t(code), tenantViewSchema),
    createTenant: (input) => call('POST', '/tenants', tenantMutationSchema, input),
    updateTenant: (code, patch) => call('PATCH', t(code), tenantMutationSchema, patch),
    deleteTenant: async (code) => {
      await call('DELETE', t(code), z.unknown());
    },
    issueEnrollmentToken: (code) =>
      call('POST', `${t(code)}/enrollment-token`, issuedEnrollmentTokenSchema),
    revokeEnrollmentToken: (code) =>
      call('DELETE', `${t(code)}/enrollment-token`, enrollmentTokenViewSchema),
    resendWelcomeEmail: (code) =>
      call('POST', `${t(code)}/welcome-email`, welcomeEmailResultSchema),
    health: (code, refresh) =>
      call('GET', `${t(code)}/health?refresh=${refresh}`, tenantHealthReportSchema),
    opsBranding: (code, body) => call('PUT', `${t(code)}/ops/branding`, opsResultSchema, body),
    opsModules: (code, body) => call('PUT', `${t(code)}/ops/modules`, opsResultSchema, body),
    middlewares: (code) => call('GET', `${t(code)}/middlewares`, tenantMiddlewaresViewSchema),
    setMiddlewares: (code, allowed) =>
      call('PUT', `${t(code)}/middlewares`, tenantMiddlewaresViewSchema, { allowed }),
    reloadGateway: () => call('POST', '/gateway/reload', gatewaySyncSchema),
    pushStats: (days) => call('GET', `/push/stats?days=${days}`, pushStatsSchema),
    audit: (filter) => {
      const query = new URLSearchParams();
      if (filter.tenantCode) query.set('tenantCode', filter.tenantCode);
      if (filter.before) query.set('before', String(filter.before));
      query.set('limit', String(filter.limit ?? 50));
      return call('GET', `/audit?${query.toString()}`, hostAuditPageSchema);
    },
    getUpdates: () => call('GET', '/updates', updatesViewSchema),
    getUpdateRun: (id) => call('GET', `/updates/runs/${encodeURIComponent(id)}`, updaterRunSchema),
    checkUpdates: () => call('POST', '/updates/check', updaterAcceptedSchema, {}),
    deployRelease: (version) => call('POST', '/updates/deploy', updaterAcceptedSchema, { version }),
    rollbackRelease: () => call('POST', '/updates/rollback', updaterAcceptedSchema, {}),
  };
}

export function toApiError(status: number, payload: unknown): ApiError {
  const envelope = errorEnvelopeSchema.safeParse(payload);
  if (envelope.success) {
    const { code, message, details } = envelope.data.error;
    return new ApiError(status, code, message, details);
  }
  return new ApiError(status, 'HTTP_ERROR', `Request failed (HTTP ${status})`);
}

/** User-facing text for any thrown value. */
export function describeError(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.status === 401) return 'Your session has expired. Sign in again.';
    if (error.status === 403) return 'You are not allowed to do this.';
    return error.message;
  }
  return error instanceof Error ? error.message : 'Something went wrong.';
}
